import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { TextKeys } from '../../core/i18n/text-keys';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DateRange } from '../../core/models/date-range';
import * as moment from 'moment-timezone';
import { TIMEZONE } from '../../core/services/language.service';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';

// Register locale with Monday-first week (CH/EU norm) and 2-letter German month abbreviations.
// moment.defineLocale() sets the global locale as a side effect, so we restore it immediately.
moment.defineLocale('de-ch-adm', {
  months: 'Januar_Februar_März_April_Mai_Juni_Juli_August_September_Oktober_November_Dezember'.split('_'),
  monthsShort: 'Jan._Feb._März_Apr._Mai_Juni_Juli_Aug._Sep._Okt._Nov._Dez.'.split('_'),
  weekdays: 'Sonntag_Montag_Dienstag_Mittwoch_Donnerstag_Freitag_Samstag'.split('_'),
  weekdaysShort: 'So._Mo._Di._Mi._Do._Fr._Sa.'.split('_'),
  weekdaysMin: 'So_Mo_Di_Mi_Do_Fr_Sa'.split('_'),
  longDateFormat: {
    LT: 'HH:mm', LTS: 'HH:mm:ss',
    L: 'DD.MM.YYYY', LL: 'D. MMMM YYYY',
    LLL: 'D. MMMM YYYY HH:mm', LLLL: 'dddd, D. MMMM YYYY HH:mm',
  },
  week: { dow: 1, doy: 4 },
});
moment.locale('en'); // restore global locale

const MY_FORMATS = {
  parse: { dateInput: 'DD.MM.YYYY' },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-date-range-selection',
  templateUrl: './date-range-selection.component.html',
  styleUrls: ['./date-range-selection.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'de-ch-adm' },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
  ],
})
export class DateRangeSelectionComponent implements OnInit {
  @Input() defaultDate: DateRange;
  @Output() dateRangeChanged = new EventEmitter<DateRange>();

  texts = TextKeys;

  range: UntypedFormGroup;

  constructor() {}

  ngOnInit() {
    if (this.defaultDate) {
      this.range = new UntypedFormGroup({
        start: new UntypedFormControl(
          moment.tz(this.defaultDate.start, TIMEZONE)
        ),
        end: new UntypedFormControl(moment.tz(this.defaultDate.end, TIMEZONE)),
      });
    } else {
      this.range = new UntypedFormGroup({
        start: new UntypedFormControl(
          moment.tz(moment(), TIMEZONE).clone().startOf('month')
        ),
        end: new UntypedFormControl(
          moment.tz(moment(), TIMEZONE).clone().endOf('month')
        ),
      });
    }
  }

  onChange() {
    // console.log('date range changed');
    if (this.range?.value?.end && this.range?.value?.start) {
      this.dateRangeChanged.emit(this.getDateRange());
    }
  }

  public getDateRange(): DateRange {
    const value = this.range?.value;

    if (!value || !value.start || !value.end) {
      return { start: null, end: null } as DateRange;
    }

    const toMoment = (val: any): moment.Moment => {
      // Fall 1: ist schon ein Moment-Objekt
      if (moment.isMoment(val)) {
        return val;
      }

      // Fall 2: native Date
      if (val instanceof Date) {
        return moment(val);
      }

      // Fall 3: String oder irgendwas anderes, was moment parsen kann
      return moment(val);
    };

    const startM = toMoment(value.start);
    const endM = toMoment(value.end);

    const datRange: DateRange = {
      start: moment.tz(startM, TIMEZONE).startOf('day').format(),
      end: moment.tz(endM, TIMEZONE).startOf('day').format(),
    } as DateRange;

    // console.log('date range: ', datRange);
    return datRange;
  }

  disable() {
    this.range.disable();
  }

  enable() {
    this.range.enable();
  }
}
